/**
 * Average heart rate + the exact 1 Hz HR trace for the whole activity (read from the record
 * messages), drawn as a filled line.
 */
import { store } from '../state/store'
import { downsample, heartRateSamples } from '../analytics/heartRate'

function Sparkline({ values }: { values: number[] }) {
  const w = 480
  const h = 56
  if (values.length < 2) return <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} />
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w
    const y = h - ((v - min) / span) * (h - 8) - 4
    return [x, y] as const
  })
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ')
  const area = `${line} L${w} ${h} L0 ${h} Z`
  return (
    <svg
      width="100%"
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style="display:block"
    >
      <defs>
        <linearGradient id="hrg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--accent)" stop-opacity="0.22" />
          <stop offset="100%" stop-color="var(--accent)" stop-opacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#hrg)" />
      <path
        d={line}
        fill="none"
        stroke="var(--accent)"
        stroke-width="2"
        vector-effect="non-scaling-stroke"
        stroke-linejoin="round"
      />
    </svg>
  )
}

export function HeartRateCard() {
  const s = store.stats.value
  const activity = store.activity.value
  if (!s || !activity) return null
  if (s.avgHr === null) return null // no HR recorded in this file

  const trace = downsample(heartRateSamples(activity.file), 480)

  return (
    <div class="card hr-card">
      <div class="hr-meta">
        <div class="label" style="color:var(--accent)">
          Average heart rate
        </div>
        <div class="value">
          <b>{s.avgHr}</b>
          <small>bpm</small>
        </div>
        <div class="trend">{s.maxHr ? `peak ${s.maxHr}` : ''}</div>
      </div>
      <div class="viz">
        <Sparkline values={trace} />
      </div>
    </div>
  )
}
